
import AbstractSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import Contract from "slotworks/utils/contract";
import Rectangle from "slotworks/utils/geom/rectangle";

export default class AnimateOverSymbolBehaviour extends AbstractSymbolBehaviour {

    public static ANIMATE_BEHIND: boolean = false;

    protected masterSymbol: string = "symbol_1_1";

    protected defaultAnimation: string;

    protected animation: AnimatedSprite;

    private offset: Rectangle;

    private completeResolve: () => void;

    /**
     * Animates something over top of the static sprite during a win (for example a shimmer or particle effect)
     *
     * @param symbol {SymbolComponent}
     * @param [defaultAniamtion="win"] {string}
     */
    constructor(symbol: SymbolComponent, defaultAnimation: string = "win_over") {
        super(symbol);
        this.defaultAnimation = defaultAnimation;
    }

    public static() {
        this.cleanUp();
    }

    public highlight(): Contract<void> {
        return this.animate("_over", this.defaultAnimation);
    }

    public win(): Contract<void> {

        return this.animate("_over", this.defaultAnimation);
    }

    public cycle(): Contract<void> {

        return this.animate("_over", this.defaultAnimation);
    }

    public updateTransform(): void {
        super.updateTransform();

        if (this.behaviourSprite) {
            this.behaviourSprite.x += this.offset.x;
            this.behaviourSprite.y += this.offset.y;
            this.behaviourSprite.width *= this.offset.width;
            this.behaviourSprite.height *= this.offset.height;

            // Adjust for if this is the middle of a stack
            const stackOffset = this.symbol.getStackOffsetY();
            if (stackOffset) {
                this.behaviourSprite.y -= stackOffset;
            }
        }
    }

    protected animate(animSuffix: string | string[], defaultAnimation: string, autoReset: boolean = false, loop: boolean = false) {

        // Resolve existing animations before starting a new one
        if (this.completeResolve) {
            this.completeResolve();
        }
        this.cleanUp();

        const animNameList: string[] = [];
        if (typeof animSuffix === "string") {
            animNameList.push(this.symbol.symbolId + animSuffix);
        } else {
            for (const suffix of animSuffix) {
                animNameList.push(this.symbol.symbolId + suffix);
            }
        }
        if (this.symbol.symbolId.indexOf("expanded_") > -1) {
            animNameList.push("expanded_" + defaultAnimation);
        }
        animNameList.push(defaultAnimation);

        // Generate aniamtion to play overtop
        let animation: AnimatedSprite = GraphicsManager.createAnimation(animNameList);

        if (!animation) {
            return Contract.empty();
        }

        animation.stop();
        animation.visible = false;

        this.symbol.setLayer(Layers.SymbolAnimations);
        Layers.SymbolAnimations.add(animation, false, AnimateOverSymbolBehaviour.ANIMATE_BEHIND);

        this.behaviourSprite = animation;

        // Calculate relative size for animation versus static
        const masterRect = Layers.MatrixContent.getPosition(this.masterSymbol);
        let animRect;

        animNameList.push(this.symbol.symbolId);
        while (!animRect && animNameList.length > 0) {
            animRect = Layers.MatrixContent.getPosition(animNameList.shift());
        }

        if (!animRect) {
            animRect = new Rectangle(0, 0, animation.width, animation.height);
        }

        // Offset describes how different the animation is to the static, in both scale and position
        // so that when the static is moved or scaled, the animation will still match
        const offsetX = animRect.x - masterRect.x;
        const offsetY = animRect.y - masterRect.y;
        this.offset = new Rectangle(offsetX, offsetY, animRect.width / masterRect.width, animRect.height / masterRect.height);
        this.updateTransform();

        this.behaviourSprite.visible = true;

        animation.loop = loop;
        animation.gotoAndPlay(0);

        this.animation = animation;

        return new Contract<void>((resolve) => {
            this.completeResolve = () => resolve(null);

            animation.onComplete = () => {
                if (!loop) {
                    animation.stop();
                    animation.visible = false;
                    Layers.SymbolAnimations.remove(this.animation);
                    if (autoReset) {
                        this.symbol.setLayer(Layers.MatrixContent);
                        animation = null;
                    }
                    if (this.completeResolve) {
                        this.completeResolve();
                        this.completeResolve = null;
                    }
                }
            };
        });
    }

    private cleanUp() {
        if (this.animation) {
            this.animation.onComplete = null;
            this.animation.stop();
            Layers.SymbolAnimations.remove(this.animation);
            this.animation = null;
            this.symbol.setLayer(Layers.MatrixContent);
            this.completeResolve = null;
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/matrix/symbol/behaviours/animate-over-symbol-behaviour.ts